import { useImagePicker } from "@repo/components/src/hooks/image";
import { createFile } from "@repo/components/src/hooks/image/useImagePicker";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import fileSchema from "@repo/components/src/utils/yup/fileSchema";
import { AlertCircleIcon, EditIcon } from "lucide-react-native";
import React, { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import * as yup from "yup";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

type FileSchema = yup.InferType<typeof fileSchema>;

type FormInputAvatarProps<T extends FieldValues> = Omit<
  FormInputProps<T, FileSchema | null>,
  "size" | "placeholder" | "required"
> & {
  fallbackText: string;
  size: React.ComponentPropsWithoutRef<typeof Avatar>["size"];
  iconSize?: ComponentProps<typeof Icon>["size"];
  iconClassName?: string;
  avatarProps?: ComponentProps<typeof Avatar>;
  /** Spread onto the underlying `AvatarImage`, e.g. `{ contentFit: "contain" }`
   * for a logo that shouldn't be cropped. Additive — leaves every other
   * consumer's rendering untouched. */
  imageProps?: Partial<ComponentProps<typeof AvatarImage>>;
};

export default function FormInputAvatar<TFieldValues extends FieldValues>(
  props: FormInputAvatarProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    labelSize,
    className,
    size,
    fallbackText,
    iconSize = "xs",
    iconClassName = "",
    avatarProps,
    imageProps,
  } = props;
  const { onPress, loading } = useImagePicker({
    options: {
      mediaTypes: "images",
      quality: 0.8,
    },
  });
  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, value, ref, disabled },
        fieldState: { error },
      }) => {
        return (
          <FormControl
            isInvalid={!!error}
            isDisabled={disabled}
            className={className}
            ref={focusScrollRef(ref)}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Pressable
              onPress={async () => {
                const newFile = await onPress();
                newFile && onChange(createFile(newFile[0]));
              }}
              className="w-fit"
              disabled={disabled}
            >
              {(pressableProps: any) => (
                <Box
                  className={`${pressableProps.pressed ? "opacity-50" : "opacity-100"} w-fit`}
                >
                  <Box
                    className={`bg-primary-500 border-2 outline-none border-outline-50 rounded-full p-2 absolute z-20 -right-1 -top-1 ${iconClassName}`}
                  >
                    <Icon
                      as={EditIcon}
                      size={iconSize}
                      className="text-outline-50"
                    />
                  </Box>
                  <Skeleton className="rounded-full" isLoaded={!loading}>
                    <Avatar
                      size={size}
                      action={value ? "transparent" : "primary"}
                      className={className}
                      key={value ? value.uri : "empty"}
                      {...avatarProps}
                    >
                      <AvatarFallbackText>{fallbackText}</AvatarFallbackText>
                      <AvatarImage {...imageProps} source={value?.uri} />
                    </Avatar>
                  </Skeleton>
                </Box>
              )}
            </Pressable>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
